import React, { useState, useEffect, useRef } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { Tag } from 'primereact/tag';
import { InputText } from 'primereact/inputtext';
import MLMTreeVisualization from '../../components/tree/MLMTreeVisualization';
import MLMSidePanel from '../../components/tree/MLMSidePanel';
import { mlmService } from '../../services/mlmService';
import axiosInstance from '../../config/axios.config';

/**
 * MLMDashboardPage Component
 * MLM Network dashboard with tree visualization and performance side panel
 *
 * Features:
 * - View MLM network tree (left 70%)
 * - View node performance in side panel (right 30%)
 * - Click nodes to filter performance data
 * - Click downline cards to drill down
 * - Initialize MLM network
 *
 * Access Control:
 * - Accessible to all authenticated users
 */
const MLMDashboardPage = () => {
    const [treeData, setTreeData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showInitDialog, setShowInitDialog] = useState(false);
    const [initializing, setInitializing] = useState(false);
    const [isInMLM, setIsInMLM] = useState(false);

    // Side Panel State
    const [selectedNodeId, setSelectedNodeId] = useState(null);
    const [nodeData, setNodeData] = useState(null);
    const [nodeLoading, setNodeLoading] = useState(false);
    const [nodeError, setNodeError] = useState(null);
    const [isSearchSelection, setIsSearchSelection] = useState(false); // Flag to indicate if selection is from search

    // Lazy Loading State
    const [expandingNodeId, setExpandingNodeId] = useState(null);

    // Ref to store expand function from visualization component
    const expandNodeRef = useRef(null);

    // Search State
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [showSearchModal, setShowSearchModal] = useState(false);

    const toast = useRef(null);
    const searchDebounceTimer = useRef(null);
    const searchInputRef = useRef(null);

    // Get current user info
    const userInfo = JSON.parse(localStorage.getItem('user') || '{}');
    const currentUserId = userInfo.id;

    useEffect(() => {
        fetchMLMData();
    }, []);

    // Fetch initial node performance on mount (show my own data)
    useEffect(() => {
        if (isInMLM && currentUserId) {
            fetchNodePerformance(null); // null = fetch auth user's data
        }
    }, [isInMLM, currentUserId]);

    // Cleanup search debounce timer on unmount
    useEffect(() => {
        return () => {
            if (searchDebounceTimer.current) {
                clearTimeout(searchDebounceTimer.current);
            }
        };
    }, []);

    const fetchMLMData = async () => {
        setLoading(true);
        try {
            // Fetch shallow tree for lazy loading (only root + direct children)
            const treeResponse = await axiosInstance.get('/tree/mlm/my-tree-shallow').catch(() => ({ data: { success: false } }));

            // Tree data
            if (treeResponse.data.success && treeResponse.data.data) {
                setTreeData(treeResponse.data.data);
                // If tree data exists, user is in MLM (even if they have no children yet)
                setIsInMLM(true);
            } else {
                // No tree data means user is not in MLM network
                setTreeData(null);
                setIsInMLM(false);
            }

        } catch (error) {
            console.error('Error fetching MLM data:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.response?.data?.message || 'Failed to load MLM data',
                life: 5000
            });
        } finally {
            setLoading(false);
        }
    };

    /**
     * Fetch node performance data from API
     * @param {number|null} userId - User ID (null = auth user)
     */
    const fetchNodePerformance = async (userId = null) => {
        console.log('MLMDashboardPage: fetchNodePerformance called with userId:', userId, 'Type:', typeof userId);
        setNodeLoading(true);
        setNodeError(null);
        setSelectedNodeId(userId || currentUserId);

        try {
            console.log('MLMDashboardPage: Calling mlmService.getNodePerformance with userId:', userId);
            const response = await mlmService.getNodePerformance(userId);
            console.log('MLMDashboardPage: API response:', response);

            if (response.success) {
                setNodeData(response.data);
            } else {
                setNodeError(response.message || 'Failed to load performance data');
            }
        } catch (error) {
            console.error('MLMDashboardPage: Error fetching node performance:', error);
            setNodeError(error.message || 'Failed to load performance data');
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.message || 'Failed to load performance data',
                life: 3000
            });
        } finally {
            setNodeLoading(false);
        }
    };

    /**
     * Handle node click from tree visualization
     */
    const handleNodeClick = (nodeId) => {
        console.log('MLMDashboardPage: handleNodeClick called with nodeId:', nodeId, 'Type:', typeof nodeId);
        if (nodeId) {
            setSelectedNodeId(nodeId);
            setIsSearchSelection(false); // Regular node click, NOT from search
            fetchNodePerformance(nodeId);
        } else {
            console.warn('MLMDashboardPage: handleNodeClick called with null/undefined nodeId');
        }
    };

    /**
     * Handle search input change with debouncing
     */
    const handleSearchInputChange = (e) => {
        const query = e.target.value;
        setSearchQuery(query);

        // Clear any existing timer
        if (searchDebounceTimer.current) {
            clearTimeout(searchDebounceTimer.current);
        }

        // Minimum 2 characters required
        if (!query || query.length < 2) {
            setSearchResults([]);
            setSearchLoading(false);
            return;
        }

        // Set loading state immediately
        setSearchLoading(true);

        // Debounce the API call
        searchDebounceTimer.current = setTimeout(async () => {
            try {
                console.log('Searching for:', query);
                const response = await mlmService.searchUsers(query, 10);
                console.log('Search response:', response);

                if (response.success && response.data && response.data.results) {
                    setSearchResults(response.data.results);
                    console.log('Results set:', response.data.results);
                } else {
                    setSearchResults([]);
                }
            } catch (error) {
                console.error('Search error:', error);
                setSearchResults([]);
            } finally {
                setSearchLoading(false);
            }
        }, 300);
    };

    /**
     * Handle search result selection
     */
    const handleUserSelect = (userId) => {
        console.log('User selected from search:', userId);
        if (userId) {
            setSelectedNodeId(userId);
            setIsSearchSelection(true); // THIS IS A SEARCH SELECTION - trigger path finder
            fetchNodePerformance(userId);
            // Close modal and clear search
            setShowSearchModal(false);
            setSearchQuery('');
            setSearchResults([]);
        }
    };

    /**
     * Load children when user expands a node (Lazy Loading)
     * @param {number} nodeId - Node ID to load children for
     */
    const loadNodeChildren = async (nodeId) => {
        console.log('Loading children for node:', nodeId);
        setExpandingNodeId(nodeId);

        try {
            // Call existing /children/{userId} endpoint without limit to get ALL children
            const response = await axiosInstance.get(`/tree/mlm/children/${nodeId}`);

            if (response.data.success) {
                console.log('Children loaded:', response.data.data.children);
                // Insert children into tree
                updateTreeWithChildren(nodeId, response.data.data.children);

                // CRITICAL: Wait for React to commit the state update before expanding
                // Increased delay to 100ms to ensure tree data is fully rendered
                setTimeout(() => {
                    if (expandNodeRef.current) {
                        console.log('Expanding node after data load:', nodeId);
                        expandNodeRef.current(nodeId);
                    } else {
                        console.warn('expandNodeRef.current is null - cannot expand node');
                    }
                }, 100);
            } else {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: response.data.message || 'Failed to load children',
                    life: 3000
                });
            }
        } catch (error) {
            console.error('Error loading children:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.response?.data?.message || 'Failed to load children',
                life: 3000
            });
        } finally {
            setExpandingNodeId(null);
        }
    };

    /**
     * Insert loaded children into tree data
     * @param {number} nodeId - Parent node ID
     * @param {Array} children - Children to insert
     */
    const updateTreeWithChildren = (nodeId, children) => {
        console.log('Updating tree with children for node:', nodeId, children);

        const updateNode = (node) => {
            if (node.id === nodeId) {
                // Found the node - update its children
                return { ...node, children };
            }

            if (node.children && node.children.length > 0) {
                // Recursively search in children
                return {
                    ...node,
                    children: node.children.map(updateNode)
                };
            }

            return node;
        };

        setTreeData(prevTree => {
            if (!prevTree) return prevTree;
            return updateNode(prevTree);
        });
    };

    const handleInitializeMLM = async () => {
        setInitializing(true);
        try {
            const response = await axiosInstance.post('/tree/mlm/initialize');

            if (response.data.success) {
                toast.current?.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'MLM network initialized successfully',
                    life: 3000
                });

                setShowInitDialog(false);
                setIsInMLM(true);
                fetchMLMData();
            }
        } catch (error) {
            console.error('Error initializing MLM:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.response?.data?.message || 'Failed to initialize MLM network',
                life: 5000
            });
        } finally {
            setInitializing(false);
        }
    };

    if (loading) {
        return (
            <div className="flex align-items-center justify-content-center" style={{ minHeight: '80vh' }}>
                <ProgressSpinner />
            </div>
        );
    }

    return (
        <div className="mlm-page flex flex-column" style={{ height: 'calc(100vh - 120px)', overflow: 'hidden' }}>
            <Toast ref={toast} />

            {/* Toolbar - Exactly like OrgChart */}
            <div className="surface-card border-round shadow-1 p-2 md:p-3 mb-2 md:mb-3 flex-shrink-0">
                <div className="flex flex-column md:flex-row align-items-start md:align-items-center justify-content-between gap-2">
                    <div className="flex align-items-center gap-2 md:gap-3">
                        <i className="pi pi-share-alt text-xl md:text-2xl text-primary"></i>
                        <h2 className="m-0 text-lg md:text-xl font-semibold text-900">My MLM Network</h2>
                    </div>
                    <div className="flex align-items-center gap-2 flex-wrap">
                        <Button
                            icon="pi pi-refresh"
                            className="p-button-text p-button-sm"
                            onClick={fetchMLMData}
                            tooltip="Refresh"
                            tooltipOptions={{ position: 'bottom' }}
                        />
                        {!isInMLM && (
                            <Button
                                label={window.innerWidth < 768 ? '' : 'Initialize MLM'}
                                icon="pi pi-plus-circle"
                                onClick={() => setShowInitDialog(true)}
                                className="p-button-success p-button-sm"
                                tooltip={window.innerWidth < 768 ? 'Initialize MLM' : undefined}
                                tooltipOptions={{ position: 'bottom' }}
                            />
                        )}
                    </div>
                </div>
            </div>

            {/* Not in MLM message */}
            {!isInMLM && (
                <Message
                    severity="info"
                    text="You are not part of any MLM network yet. Initialize your network to get started."
                    className="mb-2 md:mb-3"
                />
            )}

            {/* Main Content - Exactly like OrgChart */}
            {isInMLM && (
                <div className="flex flex-column lg:flex-row gap-2 md:gap-3 flex-1 overflow-hidden mlm-content">
                    {/* Left: Tree - No extra padding, just like OrgChart */}
                    <div className="surface-card border-round shadow-1 overflow-hidden mlm-tree-container" style={{ minHeight: '300px' }}>
                        <MLMTreeVisualization
                            treeData={treeData}
                            currentUserId={currentUserId}
                            selectedUserId={selectedNodeId}
                            isSearchSelection={isSearchSelection}
                            loading={loading}
                            onNodeClick={handleNodeClick}
                            onLoadChildren={loadNodeChildren}
                            expandingNodeId={expandingNodeId}
                            expandNodeRef={expandNodeRef}
                            onSearchSelect={handleUserSelect}
                        />
                    </div>

                    {/* Right: Side Panel - p-3 like OrgChart */}
                    <div className="surface-card border-round shadow-1 overflow-auto p-3 mlm-panel-container">
                        <MLMSidePanel
                            nodeData={nodeData}
                            loading={nodeLoading}
                            error={nodeError}
                        />
                    </div>
                </div>
            )}

            {/* Responsive CSS - Exactly like OrgChart */}
            <style>{`
                .mlm-content {
                    flex-direction: column;
                }
                .mlm-tree-container {
                    flex: 1 1 auto;
                    min-height: 50vh;
                    height: auto;
                }
                .mlm-panel-container {
                    flex: 0 0 auto;
                    max-height: 40vh;
                }

                @media (min-width: 992px) {
                    .mlm-content {
                        flex-direction: row;
                    }
                    .mlm-tree-container {
                        flex: 0 0 60%;
                        min-height: auto;
                        height: 100%;
                    }
                    .mlm-panel-container {
                        flex: 0 0 40%;
                        max-height: none;
                        height: 100%;
                    }
                }
            `}</style>

                {/* Initialize MLM Dialog - Responsive */}
                <Dialog
                    header="Initialize MLM Network"
                    visible={showInitDialog}
                    style={{ width: '450px' }}
                    breakpoints={{ '768px': '90vw', '576px': '95vw' }}
                    onHide={() => setShowInitDialog(false)}
                    footer={
                        <div>
                            <Button
                                label="Cancel"
                                icon="pi pi-times"
                                onClick={() => setShowInitDialog(false)}
                                className="p-button-text"
                            />
                            <Button
                                label="Initialize"
                                icon="pi pi-check"
                                onClick={handleInitializeMLM}
                                loading={initializing}
                            />
                        </div>
                    }
                >
                    <div className="flex align-items-center gap-3 mb-3">
                        <i className="pi pi-info-circle text-4xl text-blue-500"></i>
                        <div>
                            <h4 className="m-0 mb-2">Start Your MLM Network</h4>
                            <p className="m-0 text-600">
                                This will create you as the root of a new MLM network.
                                You'll be able to add referrals and build your downline.
                            </p>
                        </div>
                    </div>

                    <Message
                        severity="info"
                        text="Once initialized, you can share your referral link with others to grow your network."
                        className="mt-3"
                    />
                </Dialog>

                {/* Search Modal - Responsive */}
                <Dialog
                    header={
                        <div className="flex align-items-center gap-2">
                            <i className="pi pi-search text-primary" style={{ fontSize: '1.2rem' }}></i>
                            <span className="text-sm md:text-base">Search Network Members</span>
                        </div>
                    }
                    visible={showSearchModal}
                    style={{ width: '50rem' }}
                    breakpoints={{ '768px': '90vw', '576px': '95vw' }}
                    onHide={() => {
                        setShowSearchModal(false);
                        setSearchQuery('');
                        setSearchResults([]);
                    }}
                    onShow={() => {
                        setTimeout(() => {
                            searchInputRef.current?.focus();
                        }, 100);
                    }}
                    modal
                    dismissableMask
                    blockScroll
                >
                    {/* Search Input - Using p-inputgroup for proper click handling */}
                    <div className="mb-3 md:mb-4">
                        <div className="p-inputgroup">
                            <span className="p-inputgroup-addon">
                                <i className="pi pi-search" style={{ color: '#6b7280' }}></i>
                            </span>
                            <InputText
                                ref={searchInputRef}
                                value={searchQuery}
                                onChange={handleSearchInputChange}
                                placeholder="Search by name, email or mobile..."
                                className="w-full text-sm md:text-base"
                            />
                            {searchLoading && (
                                <span className="p-inputgroup-addon">
                                    <i className="pi pi-spin pi-spinner" style={{ color: '#3B82F6' }}></i>
                                </span>
                            )}
                            {!searchLoading && searchQuery && (
                                <span
                                    className="p-inputgroup-addon cursor-pointer"
                                    onClick={() => {
                                        setSearchQuery('');
                                        setSearchResults([]);
                                    }}
                                >
                                    <i className="pi pi-times" style={{ color: '#6b7280' }}></i>
                                </span>
                            )}
                        </div>
                        {searchQuery && searchQuery.length < 2 && (
                            <small className="text-500 mt-2 block text-xs md:text-sm">Type at least 2 characters to search</small>
                        )}
                    </div>

                    {/* Search Results - Responsive */}
                    <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                        {searchResults.length > 0 ? (
                            <div>
                                <div className="text-xs md:text-sm text-500 mb-2 md:mb-3">
                                    Found {searchResults.length} member{searchResults.length !== 1 ? 's' : ''}
                                </div>
                                {searchResults.map((user) => (
                                    <div
                                        key={user.id}
                                        className="flex align-items-center gap-2 md:gap-3 p-2 md:p-3 hover:surface-100 cursor-pointer border-round mb-2"
                                        style={{ transition: 'all 0.2s ease', border: '1px solid var(--surface-border)' }}
                                        onClick={() => handleUserSelect(user.id)}
                                    >
                                        {/* Avatar - Responsive */}
                                        <div
                                            className="flex align-items-center justify-content-center border-round flex-shrink-0"
                                            style={{
                                                width: '2.5rem',
                                                height: '2.5rem',
                                                backgroundColor: user.is_active ? '#22C55E' : '#94A3B8',
                                                color: '#FFFFFF',
                                                fontSize: '1rem',
                                                fontWeight: 'bold',
                                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                            }}
                                        >
                                            {(user.name || 'U').charAt(0).toUpperCase()}
                                        </div>

                                        {/* User Info - Responsive */}
                                        <div className="flex-1 overflow-hidden" style={{ minWidth: 0 }}>
                                            <div className="flex align-items-center gap-2 mb-1">
                                                <span className="font-semibold text-900 text-sm md:text-base white-space-nowrap overflow-hidden text-overflow-ellipsis">
                                                    {user.name || 'Unknown'}
                                                </span>
                                                <Tag
                                                    value={user.is_active ? 'Active' : 'Inactive'}
                                                    severity={user.is_active ? 'success' : 'secondary'}
                                                    className="text-xs"
                                                    style={{ padding: '0.15rem 0.4rem' }}
                                                />
                                            </div>
                                            <div className="flex flex-column gap-1">
                                                <div className="text-xs md:text-sm text-600 flex align-items-center gap-1 white-space-nowrap overflow-hidden text-overflow-ellipsis">
                                                    <i className="pi pi-envelope" style={{ fontSize: '0.7rem' }}></i>
                                                    <span className="overflow-hidden text-overflow-ellipsis">{user.email || 'No email'}</span>
                                                </div>
                                                {user.mobile && (
                                                    <div className="text-xs md:text-sm text-600 flex align-items-center gap-1">
                                                        <i className="pi pi-phone" style={{ fontSize: '0.7rem' }}></i>
                                                        {user.mobile}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Stats - Responsive (hidden on very small screens) */}
                                        <div className="hidden sm:flex gap-2 md:gap-4 align-items-center flex-shrink-0">
                                            <div className="text-center">
                                                <div className="text-xs text-500 mb-1">Direct</div>
                                                <div className="font-bold text-sm md:text-base" style={{ color: '#3B82F6' }}>
                                                    {user.direct_referrals || 0}
                                                </div>
                                            </div>
                                            <i className="pi pi-arrow-right hidden md:block" style={{ color: '#94A3B8', fontSize: '0.8rem' }}></i>
                                            <div className="text-center">
                                                <div className="text-xs text-500 mb-1">Total</div>
                                                <div className="font-bold text-sm md:text-base" style={{ color: '#16A34A' }}>
                                                    {user.total_downline || 0}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : searchQuery.length >= 2 && !searchLoading ? (
                            <div className="text-center py-4 md:py-6">
                                <i className="pi pi-search text-400" style={{ fontSize: '2rem' }}></i>
                                <p className="text-600 mt-2 md:mt-3 mb-0 text-sm md:text-base">No members found matching "{searchQuery}"</p>
                                <small className="text-500 text-xs md:text-sm">Try searching with a different term</small>
                            </div>
                        ) : searchQuery.length < 2 ? (
                            <div className="text-center py-4 md:py-6">
                                <i className="pi pi-users text-400" style={{ fontSize: '2rem' }}></i>
                                <p className="text-600 mt-2 md:mt-3 mb-0 text-sm md:text-base">Start typing to search your network</p>
                                <small className="text-500 text-xs md:text-sm">Search by name, email, or mobile number</small>
                            </div>
                        ) : null}
                    </div>
                </Dialog>
        </div>
    );
};

export default MLMDashboardPage;
